import React, { useCallback, useEffect, useState } from "react";

import { faArrowDownWideShort, faArrowUpWideShort } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ChartFormatEnum } from "@/enum/chart";
import { commaSeperatorFormat, formatAsPercentage } from "@/helpers/common";

export interface ITableColumns {
    name: string;
    key: string;
    format: ChartFormatEnum;
    sortable?: boolean;
    type?: "string" | "number";
    render?: any;
}
interface ITableSortState {
    direction: SortDirectionType;
    sortItem?: string
}
interface ITableChart {
    row: any[] | null | undefined;
    columns: ITableColumns[];
    footer?: any;
    defaultSort?: ITableSortState;
}
type SortDirectionType = "ASC" | "DESC";

function TableChart({ columns, row, footer, defaultSort }: ITableChart) {
    const [rowData, setRowData] = useState<any[]>([]);
    const [sortState, setSortState] = useState<ITableSortState>({
        direction: "DESC",
        sortItem: undefined,
    });

    const handleSortRow = useCallback((_sortState: ITableSortState, data: any[]) => {
        const { direction, sortItem } = _sortState;
        if (data && sortItem) {
            const item = columns.find(i => i.key === sortItem);
            if (item) {
                let _rowData = [...data];
                if (item?.type === "number") {
                    _rowData = _rowData.sort((a: any, b: any) => {
                        if (direction === "DESC") {
                            return b[item.key] - a[item.key];
                        }
                        return a[item.key] - b[item.key];
                    });
                } else {
                    _rowData = _rowData.sort((a, b) => {
                        if (direction === "ASC") {
                            if (a[item.key] < b[item.key]) {
                                return -1;
                            }
                            if (a[item.key] > b[item.key]) {
                                return 1;
                            }
                            return 0;
                        } else {
                            if (a[item.key] > b[item.key]) {
                                return -1;
                            }
                            if (a[item.key] < b[item.key]) {
                                return 1;
                            }
                            return 0;
                        }

                    });
                }
                setRowData(_rowData);
            }
        }
    }, [columns]);

    useEffect(() => {
        if (defaultSort && row) {
            setSortState(defaultSort);
            handleSortRow(defaultSort, row);
        }
    }, [defaultSort, columns, handleSortRow, row]);

    const getRowData = useCallback((header: ITableColumns, rowItem: any) => {
        switch (header.format) {
            case ChartFormatEnum.number:
                return !rowItem ? rowItem : commaSeperatorFormat(rowItem);
            case ChartFormatEnum.percent:
                return formatAsPercentage((rowItem * 100).toFixed(2) as any);
            case ChartFormatEnum.text:
                return rowItem;
            default:
                return rowItem;
        }
    }, []);

    const handleSort = useCallback((key: string) => {
        let s = sortState;
        if (key === sortState.sortItem) {
            s = {
                ...s,
                direction: s.direction === "ASC" ? "DESC" : "ASC",
            };
            setSortState(s);
        } else {
            s = {
                direction: "DESC",
                sortItem: key,
            };
            setSortState(s);
        }
        handleSortRow(s, rowData);

    }, [handleSortRow, rowData, sortState]);

    return (
        <div className='tableWrapper'>
            <div className="mainTable">
                <table>
                    <thead>
                        <tr>
                            {
                                columns?.map(i => (
                                    <th
                                        key={i.key}
                                        onClick={() => i.sortable && handleSort(i.key)}>{i.name} {
                                            i.sortable && sortState.sortItem === i.key && <FontAwesomeIcon
                                                icon={sortState.direction === "ASC" ? faArrowUpWideShort : faArrowDownWideShort}
                                                className="sortIcon" />}</th>
                                ))
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {
                            rowData?.map((item, index) => (
                                <tr key={index}>
                                    {
                                        columns.map((j, _index) => (
                                            <td key={_index}>{
                                                j.render
                                                    ? j.render(getRowData(j, item[j.key]), item)
                                                    : getRowData(j, item[j.key])
                                            }</td>
                                        ))
                                    }
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
            {footer}
        </div>
    );
}

export default TableChart;
